import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import { 
  ThemeProvider, 
  Alert,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Concertina,
  FileUploadButton,
  Header,
  Input,
  Modal,
  NoteBox,
  Progress,
  StepIndicator,
  Radio,
  RadioGroup,
  ReadBy,
  RichTextArea,
  Select,
  SidebarV2,
  SimpleModal,
  StatusBar,
  TableOfContents,
  Tabs,
  Toggle,
  ExpandingBox,
  AcknowledgementBox,
  ExpandingBoxToggle,
  useTheme,
} from 'ece-docs-components';
import { HomeRounded, SettingsRounded, ArticleRounded, PeopleRounded } from '@mui/icons-material';
import { BrowserRouter, useNavigate } from 'react-router-dom';

type AlertVariant =
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'note-internallink'
  | 'note-externallink'
  | 'note-important'
  | 'note'
  | 'note-assurance'
  | 'note-share'
  | 'note-task'
  | 'custom';

type ThemeKey = 'ECE' | 'GP' | 'School' | 'Lightn';

// ── Colour Palette ─────────────────────────────────────────────────────────────

const isLightColor = (hex: string): boolean => {
  const h = hex.replace('#', '').slice(0, 6);
  if (h.length < 6) return true;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
};

const resolveColor = (color: string): string => {
  if (!color || color === 'transparent') return '#ffffff';
  if (/^#[0-9a-fA-F]{6}$/.test(color)) return color.toUpperCase();
  if (/^#[0-9a-fA-F]{8}$/.test(color)) return ('#' + color.slice(1, 7)).toUpperCase();
  if (/^#[0-9a-fA-F]{3}$/.test(color)) {
    const [, r, g, b] = color.split('');
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return color;
    ctx.fillStyle = color;
    return ctx.fillStyle.toUpperCase();
  } catch {
    return color;
  }
};

interface SwatchEntry {
  name: string;
  color: string;
  token: string;
  usage: string;
}

const ColourSwatch: React.FC<SwatchEntry> = ({ name, color, token, usage }) => {
  const [copied, setCopied] = React.useState(false);
  const resolvedHex = resolveColor(color);
  const displayHex = /^#[0-9a-fA-F]{8}$/i.test(color) ? color.toUpperCase() : resolvedHex;
  const light = isLightColor(resolvedHex);

  return (
    <Box
      onClick={() => { navigator.clipboard.writeText(displayHex); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 2, p: 2,
        borderRadius: 2, border: '1px solid', borderColor: 'divider',
        cursor: 'pointer', userSelect: 'none', backgroundColor: 'background.paper',
        transition: 'box-shadow 0.2s, transform 0.15s',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.10)', transform: 'translateY(-1px)' },
      }}
    >
      <Box sx={{
        width: 64, height: 64, borderRadius: 2, flexShrink: 0,
        backgroundColor: color, border: '1px solid rgba(0,0,0,0.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {copied && (
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: light ? '#000' : '#fff' }}>Copied!</Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography fontWeight={700} sx={{ fontSize: '0.9rem', color: 'text.primary' }}>{name}</Typography>
        <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>{displayHex}</Typography>
        <Typography sx={{ fontSize: '0.70rem', fontFamily: 'monospace', color: 'text.disabled', mb: 0.5 }}>{token}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', wordBreak: 'break-word', lineHeight: 1.4 }}>{usage}</Typography>
      </Box>
    </Box>
  );
};

const ColourPalette: React.FC = () => {
  const theme = useTheme();
  const p = theme.palette as any;

  const groups: { heading: string; entries: SwatchEntry[] }[] = [
    {
      heading: 'Brand',
      entries: [
        { name: 'Primary',            token: 'palette.primary.main',           color: p.primary?.main,           usage: 'Primary buttons, active sidebar highlight, progress bar fill, checked checkbox/radio, link colour, markAsRead border' },
        { name: 'Primary Contrast',   token: 'palette.primary.contrastText',   color: p.primary?.contrastText,   usage: 'Text on primary-coloured backgrounds (button labels, header text on primary BG)' },
        { name: 'Secondary',          token: 'palette.secondary.main',         color: p.secondary?.main,         usage: 'Secondary buttons, toggle track (on state), secondary action surfaces' },
        { name: 'Secondary Contrast', token: 'palette.secondary.contrastText', color: p.secondary?.contrastText, usage: 'Text on secondary-coloured surfaces' },
        { name: 'Accent',             token: 'palette.accent.main',            color: p.accent?.main,            usage: 'Tab button active background, tab content area, sidebar item hover tint' },
      ],
    },
    {
      heading: 'Text',
      entries: [
        { name: 'Text Primary',   token: 'palette.text.primary',   color: p.text?.primary,   usage: 'All body text, headings h1–h6, sidebar labels, input labels, breadcrumb text, toggle label, button text colour' },
        { name: 'Text Secondary', token: 'palette.text.secondary', color: p.text?.secondary, usage: 'Helper text, body2 paragraphs, inactive tab labels, caption text, subtitle2, overline text' },
        { name: 'Text Disabled',  token: 'palette.text.disabled',  color: p.text?.disabled,  usage: 'Disabled input/select placeholder text, disabled component labels and icons' },
      ],
    },
    {
      heading: 'Backgrounds',
      entries: [
        { name: 'Background Default', token: 'palette.background.default', color: p.background?.default, usage: 'Page/app background (body background via CssBaseline)' },
        { name: 'Background Paper',   token: 'palette.background.paper',   color: p.background?.paper,   usage: 'Card surfaces, modal dialogs, input field backgrounds, sidebar background, toggle button background' },
        { name: 'Light',              token: 'palette.light.main',          color: p.light?.main,         usage: 'ToggleButton background alias, off-state toggle track, logo background surfaces' },
      ],
    },
    {
      heading: 'Status',
      entries: [
        { name: 'Error (Red)',      token: 'palette.error.main',      color: p.error?.main,      usage: 'Danger buttons, error input/select borders, Action Required NoteBox highlight, error alert border and icon' },
        { name: 'Warning (Yellow)', token: 'palette.warning.main',    color: p.warning?.main,    usage: 'Optional status chip, Declined NoteBox highlight, warning alert background/border' },
        { name: 'Info (Orange)',    token: 'palette.info.main',       color: p.info?.main,       usage: 'Mandatory status chip, Not Started NoteBox highlight, info alert accent colour' },
        { name: 'Success (Green)',  token: 'palette.success.main',    color: p.success?.main,    usage: 'Accepted NoteBox highlight, success alert border/icon, Mark as Read visual indicator' },
        { name: 'Marked Read',      token: 'palette.markedRead.main', color: p.markedRead?.main, usage: 'Mark as Read / Marked as Read button border and check indicator colour' },
      ],
    },
    {
      heading: 'Structure',
      entries: [
        { name: 'Dark',    token: 'palette.dark.main', color: p.dark?.main, usage: 'Sidebar right border, header bottom border, toggle border, icon strokes (via dark.main with opacity)' },
        { name: 'Divider', token: 'palette.divider',   color: p.divider,    usage: '<Divider> elements, modal title border-bottom, section separators throughout all components' },
      ],
    },
    {
      heading: 'Custom Tokens',
      entries: [
        { name: 'Icon Colour',           token: 'palette.custom.iconColor',                 color: p.custom?.iconColor,                usage: 'Sidebar org-change icon fill, custom themed SVG icon tints' },
        { name: 'Primary Button Text',   token: 'palette.custom.primaryButtonTextColor',    color: p.custom?.primaryButtonTextColor,    usage: 'Text inside primary variant <Button> (ensures contrast against primary background)' },
        { name: 'Read By Background',    token: 'palette.custom.readByBackgroundColor',     color: p.custom?.readByBackgroundColor,     usage: '<ReadBy> avatar/chip background (10% primary alpha)' },
        { name: 'Step Indicator Text',   token: 'palette.custom.stepIndicatorTextColor',    color: p.custom?.stepIndicatorTextColor,    usage: 'Text inside the active/completed step circle in <StepIndicator>' },
        { name: 'Mark As Read BG',       token: 'palette.custom.markAsReadBackgroundColor', color: p.custom?.markAsReadBackgroundColor, usage: 'Mark As Read button background fill (10% primary alpha)' },
        { name: 'Mark As Read Border',   token: 'palette.custom.markAsReadBorderColor',     color: p.custom?.markAsReadBorderColor,     usage: 'Mark As Read button border colour' },
        { name: 'Tab Background',        token: 'palette.custom.tabBackgroundColor',        color: p.custom?.tabBackgroundColor,        usage: 'Active tab button background, tab content panel background, <TableOfContents> active NavLink background' },
        { name: 'Status Bar Background', token: 'palette.custom.statusBarBackgroundColor',  color: p.custom?.statusBarBackgroundColor,  usage: '<StatusBar> container background colour (warm orange-cream tone)' },
      ],
    },
  ].map(group => ({
    ...group,
    entries: group.entries.filter(e => !!e.color && e.color !== 'transparent'),
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {groups.map((group) => (
        <Box key={group.heading}>
          <Typography variant="h6" sx={{ mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}>
            {group.heading}
          </Typography>
          <Grid container spacing={2}>
            {group.entries.map((entry) => (
              <Grid key={entry.token} size={{ xs: 12, sm: 6 }}>
                <ColourSwatch {...entry} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};


// ── Main Demo ──────────────────────────────────────────────────────────────────
const ComponentDemo: React.FC<{
  selectedTheme: ThemeKey;
  setSelectedTheme: (t: ThemeKey) => void;
}> = ({ selectedTheme, setSelectedTheme }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isSimpleModalOpen, setIsSimpleModalOpen] = React.useState(false);
  const [modalStatus, setModalStatus] = React.useState<
    'Accepted' | 'Pending' | 'Action Required' | 'Declined' | 'Draft' | 'Not Started'
  >('Action Required');
  const [requirementType, setRequirementType] = React.useState<'Default' | 'Optional'>('Default');
  const [progressCurrent, setProgressCurrent] = React.useState(3);
  const [currentStepIndex, setCurrentStepIndex] = React.useState(2);
  const [selectedRadio, setSelectedRadio] = React.useState('option1');
  const [selectedValue, setSelectedValue] = React.useState('');
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [isExpandingBoxOpen, setOpenBox] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('buttons');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [toggleChecked, setToggleChecked] = React.useState(false);
  const [richTextValue, setRichTextValue] = React.useState('');
  const [uploadedFileName, setUploadedFileName] = React.useState('');
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const [isModalLoading, setIsModalLoading] = React.useState(false);

  const buttonsRef         = React.useRef<HTMLDivElement>(null);
  const inputsRef          = React.useRef<HTMLDivElement>(null);
  const richTextRef        = React.useRef<HTMLDivElement>(null);
  const selectsRef         = React.useRef<HTMLDivElement>(null);
  const checkboxesRef      = React.useRef<HTMLDivElement>(null);
  const radiosRef          = React.useRef<HTMLDivElement>(null);
  const togglesRef         = React.useRef<HTMLDivElement>(null);
  const cardsRef           = React.useRef<HTMLDivElement>(null);
  const alertsRef          = React.useRef<HTMLDivElement>(null);
  const progressRef        = React.useRef<HTMLDivElement>(null);
  const stepIndicatorsRef  = React.useRef<HTMLDivElement>(null);
  const modalsRef          = React.useRef<HTMLDivElement>(null);
  const noteBoxesRef       = React.useRef<HTMLDivElement>(null);
  const breadcrumbsRef     = React.useRef<HTMLDivElement>(null);
  const tabsRef            = React.useRef<HTMLDivElement>(null);
  const concertinaRef      = React.useRef<HTMLDivElement>(null);
  const statusBarRef       = React.useRef<HTMLDivElement>(null);
  const readByRef          = React.useRef<HTMLDivElement>(null);
  const fileUploadRef      = React.useRef<HTMLDivElement>(null);
  const expandingBoxRef    = React.useRef<HTMLDivElement>(null);
  const acknowledgementRef = React.useRef<HTMLDivElement>(null);
  const actionButtonRef    = React.useRef<HTMLDivElement>(null);
  const colourPaletteRef   = React.useRef<HTMLDivElement>(null);
  const isScrollingRef     = React.useRef(false);

  const steps = ['Personal Info', 'Documents', 'Review', 'Submit', 'Complete'];

  const alertVariants: AlertVariant[] = [
    'info', 'success', 'warning', 'error',
    'note-internallink', 'note-externallink', 'note-important',
    'note', 'note-assurance', 'note-task', 'note-share', 'custom',
  ];

  const sidebarItems = [
    { id: 'home',     title: 'Home',     icon: <HomeRounded sx={{ fontSize: 20 }} />,     url: '/' },
    {
      id: 'policies', title: 'Policies', icon: <ArticleRounded sx={{ fontSize: 20 }} />,
      items: [
        { id: 'policy-1', title: 'Health & Safety', url: '/policies/health' },
        { id: 'policy-2', title: 'Privacy Policy',  url: '/policies/privacy' },
        { id: 'policy-3', title: 'Sub-policies', url: '/policies/sub',
          items: [
            { id: 'sub-1', title: 'Sub-policy A', url: '/policies/sub/a' },
            { id: 'sub-2', title: 'Sub-policy B', url: '/policies/sub/b',           
              items: [
                { id: 'sub-sub-1', title: 'Sub-Sub-policy A', url: '/policies/sub/sub/a' },
                { id: 'sub-sub-2', title: 'Sub-Sub-policy B', url: '/policies/sub/sub/b' },
              ],
            },
          ],
        },
      ],
    },
    { id: 'staff',    title: 'Staff',    icon: <PeopleRounded sx={{ fontSize: 20 }} />,   url: '/staff' },
    { id: 'settings', title: 'Settings', icon: <SettingsRounded sx={{ fontSize: 20 }} />, url: '/settings' },
  ];

  React.useEffect(() => {
    const sectionRefs = [
      { id: 'buttons',         ref: buttonsRef },
      { id: 'inputs',          ref: inputsRef },
      { id: 'rich-text',       ref: richTextRef },
      { id: 'selects',         ref: selectsRef },
      { id: 'checkboxes',      ref: checkboxesRef },
      { id: 'radios',          ref: radiosRef },
      { id: 'toggles',         ref: togglesRef },
      { id: 'cards',           ref: cardsRef },
      { id: 'alerts',          ref: alertsRef },
      { id: 'progress',        ref: progressRef },
      { id: 'step-indicators', ref: stepIndicatorsRef },
      { id: 'modals',          ref: modalsRef },
      { id: 'note-boxes',      ref: noteBoxesRef },
      { id: 'breadcrumbs',     ref: breadcrumbsRef },
      { id: 'tabs',            ref: tabsRef },
      { id: 'concertina',      ref: concertinaRef },
      { id: 'status-bar',      ref: statusBarRef },
      { id: 'read-by',         ref: readByRef },
      { id: 'file-upload',     ref: fileUploadRef },
      { id: 'expanding-box',   ref: expandingBoxRef },
      { id: 'acknowledgement', ref: acknowledgementRef },
      { id: 'action-button',   ref: actionButtonRef },
      { id: 'colour-palette',  ref: colourPaletteRef },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingRef.current) return; // ignore during programmatic scroll
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -60% 0px', // triggers when section enters top ~40% of viewport
        threshold: 0,
      }
    );

    sectionRefs.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);


  const handleSectionClick = (sectionId: string) => {
    isScrollingRef.current = true;
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    setActiveSection(sectionId);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 1000); // ← was 100ms, increase to ~1000ms
  };


  const handleCloseModal = () => { setIsModalOpen(false); setSuccessMessage(''); setErrorMessage(''); };

  const handleSave = async (_value: string, _checkbox: boolean) => {
    setIsModalLoading(true); setSuccessMessage(''); setErrorMessage('');
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSuccessMessage('Changes saved successfully!');
    } catch { setErrorMessage('Failed to save changes.'); }
    finally { setIsModalLoading(false); }
  };

  const handleSubmit = async (_value: string, _checkbox: boolean) => {
    setIsModalLoading(true); setSuccessMessage(''); setErrorMessage('');
    try {
      await new Promise((_, reject) => setTimeout(() => reject(new Error('fail')), 1500));
    } catch { setErrorMessage('Submission failed. Please check your input.'); }
    finally { setIsModalLoading(false); }
  };

  const tocSections = [
    { id: 'buttons',         title: 'Buttons' },
    { id: 'inputs',          title: 'Input Fields' },
    { id: 'rich-text',       title: 'Rich Text Area' },
    { id: 'selects',         title: 'Select Dropdowns' },
    { id: 'checkboxes',      title: 'Checkboxes' },
    { id: 'radios',          title: 'Radio Buttons' },
    { id: 'toggles',         title: 'Toggles' },
    { id: 'cards',           title: 'Cards' },
    { id: 'alerts',          title: 'Alerts' },
    { id: 'progress',        title: 'Progress Bars' },
    { id: 'step-indicators', title: 'Step Indicators' },
    { id: 'modals',          title: 'Modals' },
    { id: 'note-boxes',      title: 'Note Boxes' },
    { id: 'breadcrumbs',     title: 'Breadcrumbs' },
    { id: 'tabs',            title: 'Tabs' },
    { id: 'concertina',      title: 'Concertina' },
    { id: 'status-bar',      title: 'Status Bar' },
    { id: 'read-by',         title: 'Read By' },
    { id: 'file-upload',     title: 'File Upload Button' },
    { id: 'expanding-box',   title: 'Expanding Box' },
    { id: 'acknowledgement', title: 'Acknowledgement Box' },
    { id: 'action-button',   title: 'Action Button' },
    { id: 'colour-palette',  title: 'Colour Palette' },
  ];

  const DemoSection: React.FC<{
    id: string; title: string; description: string;
    children: React.ReactNode; sectionRef: React.RefObject<HTMLDivElement | null>;
  }> = ({ id, title, description, children, sectionRef }) => (
    <Box id={id} ref={sectionRef} sx={{ mb: 6, scrollMarginTop: '100px' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>{title}</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{description}</Typography>
      <Divider sx={{ mb: 3 }} />
      {children}
    </Box>
  );

  const CodeExample: React.FC<{ code: string }> = ({ code }) => (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, border: '1px solid', borderColor: 'grey.300', fontFamily: 'monospace', fontSize: '0.875rem', overflowX: 'auto' }}>
      <pre style={{ margin: 0 }}>{code}</pre>
    </Box>
  );

  const modalStatuses = ['Action Required', 'Pending', 'Accepted', 'Declined', 'Draft', 'Not Started'] as const;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <SidebarV2
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        centreName={"ECE Docs\nDemo Page"}
        items={sidebarItems}
        onNavigate={(path: string) => navigate(path)}
        isAdmin={true}
        currentPath={window.location.pathname}
        onOrgChange={() => alert('Change organisation')}
        hasMultipleOrganisations={true}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Header
          userName="Demo User"
          userInitials="DU"
          toggleMenu={() => setIsSidebarOpen(!isSidebarOpen)}
          signOut={() => console.log('Logged Out')}
          search={async () => [
            { label: 'Health & Safety Policy', description: 'Overview of H&S requirements.', value: '/policies/health' },
            { label: 'Privacy Policy', description: 'How we handle personal data.', value: '/policies/privacy' },
          ]}
          onResultClick={(result) => navigate(result.value)}
          onSearchSubmit={(q) => console.log('Search submit:', q)}
          signUpStatus="Active"
        />

        <Box sx={{ display: 'flex', gap: 4, p: 4, maxWidth: '1400px', mx: 'auto', flex: 1, width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* ── Theme Selector ── */}
            <Box sx={{ mb: 4, p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
              <Typography fontWeight={600} sx={{ mr: 1 }}>Theme:</Typography>
              {(['ECE', 'GP', 'School', 'Lightn'] as const).map((t) => (
                <Button key={t} variant={selectedTheme === t ? 'primary' : 'outline'} size="sm" onClick={() => setSelectedTheme(t)}>
                  {t === 'ECE' ? 'ECE Docs' : t === 'GP' ? 'GP Docs' : t === 'Lightn' ? 'Lightn' : 'School Docs'}
                </Button>
              ))}
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                All components reflect the active theme palette, border radius, and typography.
              </Typography>
            </Box>

            {/* Hero */}
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography variant="h2" gutterBottom>ECE Docs Component Library</Typography>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                A comprehensive React component library built with Material-UI
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="outline" size="lg" href="https://github.com/RedSunMaster/ece-docs-components">View on GitHub</Button>
              </Box>
            </Box>

            {/* Buttons */}
            <DemoSection id="buttons" sectionRef={buttonsRef} title="Buttons" description="Versatile button components with multiple variants, sizes, and states.">
              <Card variant="elevated" padding="md">
              <Typography variant="h6" gutterBottom>Variants</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {(['primary', 'secondary', 'outline', 'danger'] as const).map((v) => (
                <Grid key={v} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Button variant={v} fullWidth>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                  </Button>
                </Grid>
                ))}
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Disabled
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {(['primary', 'secondary', 'outline', 'danger'] as const).map((v) => (
                <Grid key={v} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Button variant={v} fullWidth disabled>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                  </Button>
                </Grid>
                ))}
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Sizes</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Button variant="primary" size="sm">Small</Button>
                <Button variant="primary" size="md">Medium</Button>
                <Button variant="primary" size="lg">Large</Button>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Read States</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="mark-read">Mark as Read</Button>
                <Button variant="marked-read">Marked as Read</Button>
              </Box>

              <CodeExample code={`<Button variant="primary" size="md">Click me</Button>\n<Button variant="danger" size="sm">Delete</Button>\n<Button variant="mark-read">Mark as Read</Button>`} />
              </Card>
            </DemoSection>

            {/* Input Fields */}
            <DemoSection id="inputs" sectionRef={inputsRef} title="Input Fields" description="Text input components with labels, error states, and helper text.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}><Input label="Standard Input" placeholder="Enter text..." fullWidth /></Grid>
                  <Grid size={{ xs: 12, md: 6 }}><Input label="With Helper Text" placeholder="Enter text..." helperText="This is a helper message" fullWidth /></Grid>
                  <Grid size={{ xs: 12, md: 6 }}><Input label="With Error" placeholder="Enter text..." error="This field is required" fullWidth /></Grid>
                  <Grid size={{ xs: 12, md: 6 }}><Input label="Disabled" placeholder="Cannot edit..." disabled fullWidth /></Grid>
                </Grid>
                <CodeExample code={`<Input label="Email" placeholder="Enter your email" helperText="We'll never share it" fullWidth />`} />
              </Card>
            </DemoSection>

            {/* Rich Text Area */}
            <DemoSection id="rich-text" sectionRef={richTextRef} title="Rich Text Area" description="A multi-line textarea with label, error, and helper text support.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <RichTextArea label="Standard Textarea" placeholder="Start typing..." value={richTextValue} onChange={(_q, val) => setRichTextValue(val)} question={null} fullWidth rows={4} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <RichTextArea label="With Error" placeholder="Required field..." value="" onChange={() => {}} question={null} error="This field cannot be empty" fullWidth rows={4} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <RichTextArea label="Disabled" value="This content cannot be edited." onChange={() => {}} question={null} disabled fullWidth rows={3} />
                  </Grid>
                </Grid>
                <CodeExample code={`<RichTextArea label="Notes" value={value} onChange={(q, val) => setValue(val)} question={q} rows={5} fullWidth />`} />
              </Card>
            </DemoSection>

            {/* Select Dropdowns */}
            <DemoSection id="selects" sectionRef={selectsRef} title="Select Dropdowns" description="Dropdown select components with customisable options and states.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select label="Choose an option" options={[{ value: '', label: 'Select...' }, { value: 'option1', label: 'Option 1' }, { value: 'option2', label: 'Option 2' }, { value: 'option3', label: 'Option 3' }]} value={selectedValue} onChange={(e: any) => setSelectedValue(e.target.value)} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select label="With Helper Text" options={[{ value: 'nz', label: 'New Zealand' }, { value: 'au', label: 'Australia' }, { value: 'us', label: 'United States' }]} helperText="Select your country" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select label="With Error" options={[{ value: '', label: 'Select...' }]} error="Please select an option" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select label="Disabled" options={[{ value: 'nz', label: 'New Zealand' }]} value="nz" disabled fullWidth />
                  </Grid>
                </Grid>
                <CodeExample code={`<Select label="Country" options={[{ value: 'nz', label: 'New Zealand' }]} value={v} onChange={(e) => setV(e.target.value)} fullWidth />`} />
              </Card>
            </DemoSection>

            {/* Checkboxes */}
            <DemoSection id="checkboxes" sectionRef={checkboxesRef} title="Checkboxes" description="Checkbox components with optional descriptions and custom styling.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}><Checkbox label="Simple Checkbox" defaultChecked /></Grid>
                  <Grid size={{ xs: 12, md: 6 }}><Checkbox label="With Description" description="This checkbox includes additional context for the user" /></Grid>
                  <Grid size={{ xs: 12, md: 6 }}><Checkbox label="Unchecked" /></Grid>
                  <Grid size={{ xs: 12, md: 6 }}><Checkbox label="Disabled" disabled /></Grid>
                </Grid>
                <CodeExample code={`<Checkbox label="Accept Terms" description="I agree to the terms" defaultChecked />`} />
              </Card>
            </DemoSection>

            {/* Radio Buttons */}
            <DemoSection id="radios" sectionRef={radiosRef} title="Radio Buttons" description="Radio button groups for single-choice selections.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <RadioGroup label="Choose your preference">
                      {(['option1', 'option2', 'option3'] as const).map((val, i) => (
                        <Radio key={val} label={`Option ${i + 1}`} description={i < 2 ? `This is option ${i + 1}` : undefined} name="demo-radio" value={val} checked={selectedRadio === val} onChange={(e: any) => setSelectedRadio(e.target.value)} />
                      ))}
                    </RadioGroup>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <RadioGroup label="Disabled Group" error="Please select one">
                      <Radio label="Disabled A" name="disabled-radio" value="a" disabled />
                      <Radio label="Disabled B" name="disabled-radio" value="b" disabled />
                    </RadioGroup>
                  </Grid>
                </Grid>
                <CodeExample code={`<RadioGroup label="Size"><Radio label="Small" name="size" value="sm" checked={size === 'sm'} onChange={(e) => setSize(e.target.value)} /></RadioGroup>`} />
              </Card>
            </DemoSection>

            {/* Toggles */}
            <DemoSection id="toggles" sectionRef={togglesRef} title="Toggle Switches" description="Toggle switches for binary on/off settings.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}><Toggle label="Enable notifications" checked={toggleChecked} onChange={(c) => setToggleChecked(c)} /></Grid>
                  <Grid size={{ xs: 12, md: 6 }}><Toggle label="Admin view (off)" checked={false} /></Grid>
                  <Grid size={{ xs: 12, md: 6 }}><Toggle label="Dark mode (on)" checked={true} /></Grid>
                </Grid>
                <CodeExample code={`<Toggle label="Enable feature" checked={isEnabled} onChange={(c) => setIsEnabled(c)} />`} />
              </Card>
            </DemoSection>

            {/* Cards */}
            <DemoSection id="cards" sectionRef={cardsRef} title="Cards" description="Container components with different variants and padding options.">
              <Grid container spacing={3}>
                {(['default', 'bordered', 'elevated'] as const).map((v) => (
                  <Grid key={v} size={{ xs: 12, md: 4 }}>
                    <Card variant={v} padding="md">
                      <Typography variant="h6" gutterBottom>{v.charAt(0).toUpperCase() + v.slice(1)} Card</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {v === 'default' && 'Standard styling, subtle border.'}
                        {v === 'bordered' && 'More visible border, no shadow.'}
                        {v === 'elevated' && 'Shadow elevation for depth.'}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>Padding Options</Typography>
                <Grid container spacing={3}>
                  {(['none', 'sm', 'md', 'lg'] as const).map((pad) => (
                    <Grid key={pad} size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card variant="bordered" padding={pad}><Typography variant="body2">padding="{pad}"</Typography></Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              <CodeExample code={`<Card variant="elevated" padding="lg"><Typography variant="h6">Title</Typography></Card>`} />
            </DemoSection>

            {/* Alerts */}
            <DemoSection id="alerts" sectionRef={alertsRef} title="Alerts" description="Alert messages for all variant types including all note sub-variants.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={2}>
                  {alertVariants.map((variant) => (
                    <Grid size={{ xs: 12 }} key={variant}>
                      <Alert variant={variant}>
                        This is a <strong>{variant}</strong> alert — use it for contextual messaging.
                      </Alert>
                    </Grid>
                  ))}
                </Grid>
                <CodeExample code={`<Alert variant="success">Saved!</Alert>\n<Alert variant="note-important">Critical note.</Alert>\n<Alert variant="note-task">Action pending.</Alert>`} />
              </Card>
            </DemoSection>

            {/* Progress Bars */}
            <DemoSection id="progress" sectionRef={progressRef} title="Progress Bars" description="Visual indicators for progress tracking and completion status.">
              <Card variant="elevated" padding="md">
                <Progress current={progressCurrent} total={10} showLabel />
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button variant="secondary" size="sm" onClick={() => setProgressCurrent(Math.max(0, progressCurrent - 1))}>Previous</Button>
                  <Button variant="primary"   size="sm" onClick={() => setProgressCurrent(Math.min(10, progressCurrent + 1))}>Next</Button>
                </Box>
                <CodeExample code={`<Progress current={currentStep} total={totalSteps} showLabel />`} />
              </Card>
            </DemoSection>

            {/* Step Indicators */}
            <DemoSection id="step-indicators" sectionRef={stepIndicatorsRef} title="Step Indicators" description="Multi-step process visualisation with clickable, labelled steps.">
              <Card variant="elevated" padding="md">
                <StepIndicator steps={steps} currentStep={currentStepIndex + 1} onStepClick={(step) => setCurrentStepIndex(step - 1)} />
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button variant="secondary" size="sm" onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))} disabled={currentStepIndex === 0}>Previous</Button>
                  <Button variant="primary"   size="sm" onClick={() => setCurrentStepIndex(Math.min(steps.length - 1, currentStepIndex + 1))} disabled={currentStepIndex === steps.length - 1}>Next</Button>
                </Box>
                <CodeExample code={`<StepIndicator steps={['Step 1','Step 2','Step 3']} currentStep={2} onStepClick={(s) => setCurrent(s)} />`} />
              </Card>
            </DemoSection>

            {/* Modals */}
            <DemoSection id="modals" sectionRef={modalsRef} title="Modals" description="Dialog components — full variable Modal and lightweight SimpleModal.">
              <Card variant="elevated" padding="md">
                <Typography variant="h6" sx={{ mb: 2 }}>Default Requirement</Typography>
                <Grid container spacing={2}>
                  {modalStatuses.map((status) => (
                    <Grid key={status} size={{ xs: 12, sm: 6, md: 2 }}>
                      <Button variant="primary" fullWidth onClick={() => { setModalStatus(status as any); setRequirementType('Default'); setIsModalOpen(true); }}>{status}</Button>
                    </Grid>
                  ))}
                </Grid>
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Optional Requirement</Typography>
                <Grid container spacing={2}>
                  {modalStatuses.map((status) => (
                    <Grid key={status} size={{ xs: 12, sm: 6, md: 2 }}>
                      <Button variant="secondary" fullWidth onClick={() => { setModalStatus(status as any); setRequirementType('Optional'); setIsModalOpen(true); }}>{status}</Button>
                    </Grid>
                  ))}
                </Grid>
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Simple Modal</Typography>
                <Button variant="outline" onClick={() => setIsSimpleModalOpen(true)}>Open Simple Modal</Button>
                <CodeExample code={`<Modal isOpen={isOpen} onClose={close} status="Action Required" requirementType="Default" description="..." value={v} defaultValue={dv} onSave={save} onSubmit={submit} isLoading={false} />`} />
              </Card>
            </DemoSection>

            <Modal
              isOpen={isModalOpen} onClose={handleCloseModal} variableId="demo-var-1"
              status={modalStatus} requirementType={requirementType}
              description='Centres <b>must</b> update this wording to reflect their processes.<br><b>Default wording:</b><br>At our centre, we believe that each child is unique and brings their own strengths, experiences, and culture to our learning community.'
              value="This is the current value" defaultValue="This is default text content that can be edited."
              note="Important note about this wording — please review carefully."
              currentPage={2} totalPages={3}
              successMessage={successMessage} errorMessage={errorMessage}
              onSave={handleSave} onSubmit={handleSubmit}
              onDeclineWording={() => alert('Wording declined')}
              onPrevious={() => alert('Previous')} onNext={() => alert('Next')}
              isLoading={isModalLoading}
            />

            <SimpleModal isOpen={isSimpleModalOpen} onClose={() => setIsSimpleModalOpen(false)} title="Simple Modal">
              <Typography paragraph>A simple modal with a title bar and close button.</Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button variant="secondary" onClick={() => setIsSimpleModalOpen(false)}>Cancel</Button>
                <Button variant="primary"   onClick={() => setIsSimpleModalOpen(false)}>Confirm</Button>
              </Box>
            </SimpleModal>

            {/* Note Boxes */}
            <DemoSection id="note-boxes" sectionRef={noteBoxesRef} title="Note Boxes" description="Inline highlighted text with status-driven colour coding and an edit button on hover.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={3}>
                  {([
                    { status: 'Accepted',       req: 'Default'  },
                    { status: 'Pending',         req: 'Default'  },
                    { status: 'Action Required', req: 'Default'  },
                    { status: 'Declined',        req: 'Default'  },
                    { status: 'Draft',           req: 'Default'  },
                    { status: 'Not Started',     req: 'Default'  },
                    { status: 'Not Started',     req: 'Optional' },
                  ] as const).map(({ status, req }) => (
                    <Grid size={{ xs: 12, md: 6 }} key={`${status}-${req}`}>
                      <Typography>
                        {status} / {req}:{' '}
                        <NoteBox status={status} requirementType={req} onEditClick={() => alert(`Edit: ${status}`)}>
                          Any animals visiting must be approved by the centre manager.
                        </NoteBox>
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                <CodeExample code={`<NoteBox status="Pending" requirementType="Default" onEditClick={openModal}>Policy wording here.</NoteBox>`} />
              </Card>
            </DemoSection>

            {/* Breadcrumbs */}
            <DemoSection id="breadcrumbs" sectionRef={breadcrumbsRef} title="Breadcrumbs" description="Navigation breadcrumbs with home button and optional in-section dropdown.">
              <Card variant="elevated" padding="md">
                <Breadcrumb
                  items={[{ label: 'Policies', href: '/' }, { label: 'Governance & Administration', href: '/governance' }, { label: 'Te Tiriti o Waitangi', href: '/governance/te-tiriti' }]}
                  currentItem="Te Tiriti o Waitangi"
                  dropdownItems={[{ label: 'Overview', href: '/governance/te-tiriti/overview' }, { label: 'Requirements', href: '/governance/te-tiriti/requirements' }, { label: 'Resources', href: '/governance/te-tiriti/resources' }]}
                  onItemSelect={(item) => alert(`Selected: ${item.label}`)}
                  onNavigate={(path) => navigate(path)}
                />
                <CodeExample code={`<Breadcrumb items={[{ label: 'Home', href: '/' }]} currentItem="Page" dropdownItems={[...]} onNavigate={navigate} />`} />
              </Card>
            </DemoSection>

            {/* Tabs */}
            <DemoSection id="tabs" sectionRef={tabsRef} title="Tabs" description="Tabbed content containers — convert to accordions below 1200px.">
              <Card variant="elevated" padding="md">
                <Tabs
                  tabs={[
                    { id: 'tab1', label: 'Overview', content: <Typography>Overview tab. Resize below 1200px to see accordion layout.</Typography> },
                    { id: 'tab2', label: 'Details',  content: <Typography>Detailed information goes here.</Typography> },
                    { id: 'tab3', label: 'Settings', content: <Typography>Configuration options appear here.</Typography> },
                  ]}
                  defaultTab="tab1"
                />
                <CodeExample code={`<Tabs tabs={[{ id: 'tab1', label: 'Overview', content: <Overview /> }]} defaultTab="tab1" />`} />
              </Card>
            </DemoSection>

            {/* Concertina */}
            <DemoSection id="concertina" sectionRef={concertinaRef} title="Concertina (Accordion)" description="Expandable sections — open on desktop, closed on mobile. Click # to copy a jump link.">
              <Card variant="elevated" padding="md">
                <Concertina sections={[
                  { id: 'sec-1', title: 'Philosophy Statement', content: <Typography>Our philosophy underpins everything we do. It reflects shared values and commitment to whānau.</Typography> },
                  { id: 'sec-2', title: 'Health & Safety Procedures', content: <Typography>All staff must complete site induction before commencing work. Hazard reporting within 24 hours.</Typography> },
                  { id: 'sec-3', title: 'Complaints Process', content: <Typography>Direct complaints to the centre manager first. Escalate to governing body if unresolved within 10 working days.</Typography> },
                ]} />
                <CodeExample code={`<Concertina sections={[{ id: 'sec-1', title: 'Title', content: <p>Body</p> }]} />`} />
              </Card>
            </DemoSection>

            {/* Status Bar */}
            <DemoSection id="status-bar" sectionRef={statusBarRef} title="Status Bar" description="Full-width banner — 'actionstarted' shows a next arrow, 'noaction' shows a Tailor policies button.">
              <Card variant="elevated" padding="md">
                <Typography variant="h6" gutterBottom>actionstarted</Typography>
                <StatusBar itemCount={8} variant="actionstarted" onNextClick={() => alert('Next!')} />
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>noaction</Typography>
                <StatusBar itemCount={1} variant="noaction" onTailorClick={() => alert('Tailor!')} />
                <CodeExample code={`<StatusBar itemCount={5} variant="actionstarted" onNextClick={handleNext} />`} />
              </Card>
            </DemoSection>

            {/* Read By */}
            <DemoSection id="read-by" sectionRef={readByRef} title="Read By" description="Shows who has read a document, with smart truncation and expand/collapse.">
              <Card variant="elevated" padding="md">
                <ReadBy names={['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown', 'Diana Prince', 'Eve Adams']} />
                <CodeExample code={`<ReadBy names={['Alice', 'Bob', 'Charlie']} />`} />
              </Card>
            </DemoSection>

            {/* File Upload Button */}
            <DemoSection id="file-upload" sectionRef={fileUploadRef} title="File Upload Button" description="Hidden file input wrapped with a styled button. Enforces 25 MB limit with toast feedback.">
              <Card variant="elevated" padding="md">
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>Primary — single file</Typography>
                    <FileUploadButton label="Upload Document" accept=".pdf,.doc,.docx" onFileSelect={(f) => { if (f && f[0]) setUploadedFileName(f[0].name); }} variant="primary" />
                    {uploadedFileName && <Typography variant="body2" sx={{ mt: 1 }}>Selected: {uploadedFileName}</Typography>}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>Outline — multiple files</Typography>
                    <FileUploadButton label="Upload Images" accept="image/*" multiple onFileSelect={(f) => console.log(f)} variant="outline" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>Secondary (sm)</Typography>
                    <FileUploadButton label="Upload CSV" accept=".csv" variant="secondary" size="sm" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>Danger (sm)</Typography>
                    <FileUploadButton label="Replace File" variant="danger" size="sm" />
                  </Grid>
                </Grid>
                <CodeExample code={`<FileUploadButton label="Upload" accept=".pdf" variant="primary" onFileSelect={(files) => handle(files)} />`} />
              </Card>
            </DemoSection>

            {/* Expanding Box */}
            <DemoSection id="expanding-box" sectionRef={expandingBoxRef} title="Expanding Box + Toggle" description="Inline toggle that reveals a bordered content panel.">
              <Card variant="elevated" padding="md">
                <Typography>
                  We maintain a mentally healthy workplace.{' '}
                  <ExpandingBoxToggle term="Read more" open={isExpandingBoxOpen} setOpen={() => setOpenBox(!isExpandingBoxOpen)} />
                  {' '}Staff wellbeing is central to our operations.
                </Typography>
                <ExpandingBox open={isExpandingBoxOpen} content={
                  <Box>
                    <Typography fontWeight={700} gutterBottom>We do this by:</Typography>
                    <ul>
                      <li>Not tolerating bullying, harassment, or discrimination</li>
                      <li>Encouraging staff to take regular breaks</li>
                      <li>Promoting a healthy work-life balance</li>
                      <li>Providing information about support services</li>
                    </ul>
                  </Box>
                } />
                <CodeExample code={`<ExpandingBoxToggle term="Read more" open={open} setOpen={() => setOpen(!open)} />\n<ExpandingBox open={open} content={<p>Content</p>} />`} />
              </Card>
            </DemoSection>

            {/* Acknowledgement Box */}
            <DemoSection id="acknowledgement" sectionRef={acknowledgementRef} title="Acknowledgement Box" description="Info-icon bordered box for acknowledgements, citations, or important notices.">
              <Card variant="elevated" padding="md">
                <AcknowledgementBox>
                  {[
                    <Typography variant="body2" gutterBottom key="1">
                      This policy is based on the SchoolDocs <strong>Te Tiriti o Waitangi</strong> policy. ECEDocs and SchoolDocs acknowledge Janelle Riki-Waaka (Tainui Awhiro, Ngāti Hauiti), Kaihautū of Riki Consultancy Ltd.
                    </Typography>,
                    <Typography variant="body2" key="2">He mihi ka tika ki a Janelle mō tōna mahi āwhina ki a mātou.</Typography>,
                  ]}
                </AcknowledgementBox>
                <CodeExample code={`<AcknowledgementBox><Typography variant="body2">Acknowledgement text.</Typography></AcknowledgementBox>`} />
              </Card>
            </DemoSection>

            {/* Action Button */}
            <DemoSection id="action-button" sectionRef={actionButtonRef} title="Action Button" description="Icon-only button with border, hover colour transition, and aria-label support.">
              <Card variant="elevated" padding="md">
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* <ActionButton icon={StarRateRoundedIcon} onClick={() => alert('Starred!')} label="Favourite" />
                  <ActionButton icon={SettingsRounded}     onClick={() => alert('Settings!')} label="Settings" />
                  <ActionButton icon={ArticleRounded}      onClick={() => alert('Document!')} label="Document" /> */}
                </Box>
                <CodeExample code={`<ActionButton icon={StarIcon} label="Favourite" onClick={handleClick} />`} />
              </Card>
            </DemoSection>

            {/* ── Theme Selector ── */}
            <Box sx={{ mb: 4, p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
              <Typography fontWeight={600} sx={{ mr: 1 }}>Theme:</Typography>
              {(['ECE', 'GP', 'School', 'Lightn'] as const).map((t) => (
                <Button key={t} variant={selectedTheme === t ? 'primary' : 'outline'} size="sm" onClick={() => setSelectedTheme(t)}>
                  {t === 'ECE' ? 'ECE Docs' : t === 'GP' ? 'GP Docs' : t === 'Lightn' ? 'Lightn' : 'School Docs'}
                </Button>
              ))}
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                All components reflect the active theme palette, border radius, and typography.
              </Typography>
            </Box>

            {/* Colour Palette */}
            <DemoSection id="colour-palette" sectionRef={colourPaletteRef} title="Colour Palette" description="All palette tokens from the active theme, read live via useTheme(). Grouped by category. Click any swatch to copy its hex value.">
              <ColourPalette />
            </DemoSection>

            {/* Footer */}
            <Box sx={{ mt: 8, py: 4, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                ECE Docs Components © 2025 | Built with React & Material-UI
              </Typography>
            </Box>
          </Box>

          {/* Sticky Table of Contents */}
          <Box
            sx={{
              width: '280px',
              display: { xs: 'none', lg: 'block' },
              position: 'sticky',       // ✅ was 'relative' — now sticks to viewport
              top: '100px',             // distance from the top of the viewport when stuck
              height: 'fit-content',
              maxHeight: 'calc(100vh - 120px)',
              overflowY: 'auto',        // ✅ was 'hidden' — allows scrolling the TOC itself if it's too tall
              alignSelf: 'flex-start',  // ✅ needed so it doesn't stretch to parent height
            }}
          >
            <TableOfContents
              sections={tocSections}
              activeSection={activeSection}
              onSectionClick={handleSectionClick}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  const [selectedTheme, setSelectedTheme] = React.useState<ThemeKey>('ECE');
  return (
    <ThemeProvider theme={selectedTheme}>
      <BrowserRouter>
        <ComponentDemo selectedTheme={selectedTheme} setSelectedTheme={setSelectedTheme} />
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
